<div class="formation-switcher flex items-center gap-3">
  @if (loading()) {
    <!-- Loading state avec skeleton -->
    <div class="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-slate-100 animate-pulse">
      <div class="w-5 h-5 rounded-full bg-slate-200"></div>
      <div class="w-32 h-4 bg-slate-200 rounded"></div>
    </div>
  }
  
  @else if (!hasFormations()) {
    <!-- Aucune formation -->
    <div class="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-slate-100 text-slate-600 ring-1 ring-slate-200">
      <mat-icon class="icon-size-5">school</mat-icon>
      <span class="text-sm font-medium">Aucune formation</span>
    </div>
  }
  
  @else {
    <!-- Mat-select avec trigger custom (style mockup) -->
    <mat-select 
      [formControl]="formationControl"
      [panelClass]="['rounded-2xl', 'shadow-xl', 'ring-1', 'ring-slate-200', 'p-2', 'min-w-max', 'bg-white']"
      class="inline-flex items-center gap-3 h-10 px-4 rounded-xl bg-primary-50 ring-1 ring-primary-200 cursor-pointer hover:bg-primary-100 transition-colors"
      aria-label="Sélectionner une formation">
      
      <!-- Trigger personnalisé -->
      <mat-select-trigger>
        <span class="flex items-center gap-2 sm:gap-3 min-w-0">
          <mat-icon class="icon-size-5 text-primary-700 hidden sm:block flex-shrink-0">school</mat-icon>
          <img 
            [src]="selectedFormation()?.language_detail?.flag"
            [alt]="selectedFormation()?.language_detail?.label"
            class="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover ring-1 ring-slate-200 flex-shrink-0"
          >
          <span class="text-sm font-medium text-primary-900 truncate max-w-[120px] sm:max-w-[180px] md:max-w-[250px]" [title]="displayText()">
            {{ displayText() }}
          </span>
        </span>
      </mat-select-trigger>

      <!-- Header sticky du panel -->
      <div class="sticky top-0 z-10 bg-white/95 backdrop-blur-sm p-2 mb-1 border-b border-slate-100">
        <p class="text-xs font-semibold text-slate-500 uppercase tracking-wide">
          Choisir une formation
        </p>
      </div>

      <!-- Groupe : Formations actives -->
      @if (hasActiveFormations()) {
        <div class="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
          En cours
        </div>
        
        @for (formation of formations(); track formation.id) {
          @if (formation.status === 'active') {
            <mat-option 
              [value]="formation.id"
              class="!h-auto !py-2 !px-3 rounded-lg hover:bg-slate-50 transition-colors">
              <div class="flex items-center gap-3">
                <!-- Drapeau image -->
                <img 
                  [src]="formation.language_detail?.flag"
                  [alt]="formation.language_detail?.label"
                  class="w-6 h-6 rounded-full object-cover ring-1 ring-slate-200"
                >
                
                <!-- Nom formation -->
                <span class="flex-1 font-medium text-slate-900 truncate text-sm" [title]="formation.name">
                  {{ formation.name }}
                </span>
              </div>
            </mat-option>
          }
        }
      }

      <!-- Divider + Groupe : Formations en attente -->
      @if (hasPendingFormations()) {
        <div class="my-2 border-t border-slate-200"></div>
        
        <div class="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
          En attente
        </div>
        
        @for (formation of formations(); track formation.id) {
          @if (formation.status === 'pending') {
            <mat-option 
              [value]="formation.id"
              class="!h-auto !py-2 !px-3 rounded-lg hover:bg-slate-50 transition-colors">
              <div class="flex items-center gap-3">
                <!-- Drapeau image -->
                <img 
                  [src]="formation.language_detail?.flag"
                  [alt]="formation.language_detail?.label"
                  class="w-6 h-6 rounded-full object-cover ring-1 ring-slate-200"
                >
                
                <!-- Nom formation -->
                <span class="flex-1 font-medium text-slate-900 truncate text-sm" [title]="formation.name">
                  {{ formation.name }}
                </span>
              </div>
            </mat-option>
          }
        }
      }

      <!-- Divider + Groupe : Formations terminées -->
      @if (hasDoneFormations()) {
        <div class="my-2 border-t border-slate-200"></div>
        
        <div class="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
          Terminées
        </div>
        
        @for (formation of formations(); track formation.id) {
          @if (formation.status === 'done') {
            <mat-option 
              [value]="formation.id"
              class="!h-auto !py-2 !px-3 rounded-lg hover:bg-slate-50 transition-colors">
              <div class="flex items-center gap-3">
                <!-- Drapeau image -->
                <img 
                  [src]="formation.language_detail?.flag"
                  [alt]="formation.language_detail?.label"
                  class="w-6 h-6 rounded-full object-cover ring-1 ring-slate-200"
                >
                
                <!-- Nom formation -->
                <span class="flex-1 font-medium text-slate-900 truncate text-sm" [title]="formation.name">
                  {{ formation.name }}
                </span>
              </div>
            </mat-option>
          }
        }
      }

      <!-- Divider + Groupe : Formations suspendues -->
      @if (hasSuspendedFormations()) {
        <div class="my-2 border-t border-slate-200"></div>
        
        <div class="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
          Suspendues
        </div>
        
        @for (formation of formations(); track formation.id) {
          @if (formation.status === 'suspended') {
            <mat-option 
              [value]="formation.id"
              class="!h-auto !py-2 !px-3 rounded-lg hover:bg-slate-50 transition-colors">
              <div class="flex items-center gap-3">
                <!-- Drapeau image -->
                <img 
                  [src]="formation.language_detail?.flag"
                  [alt]="formation.language_detail?.label"
                  class="w-6 h-6 rounded-full object-cover ring-1 ring-slate-200"
                >
                
                <!-- Nom formation -->
                <span class="flex-1 font-medium text-slate-900 truncate text-sm" [title]="formation.name">
                  {{ formation.name }}
                </span>
              </div>
            </mat-option>
          }
        }
      }

    </mat-select>
  }
</div>
